/**
 * Page « Charges » — charges fixes saisies à la main (salaires, loyer,
 * logiciels, prestataires…), qu'aucune API ne connaît.
 *
 * Ordre de la page : d'abord ce qui est SAISI (la liste des charges, avec
 * leur équivalent mensuel et leur statut, et le formulaire replié dessous),
 * puis ce que ça PÈSE sur la période affichée (tuiles, répartition, douze
 * mois). Chaque charge est lissée sur la période (cf. `charges.helpers.ts`
 * côté API) : les tuiles se comparent comme partout, et le total pèse dans le
 * « Résultat net » du Résumé.
 */

import {
  CHARGE_CATEGORIES,
  CHARGE_CATEGORY_LABELS,
  CHARGE_FREQUENCY_LABELS,
  type ChargesTotals,
  type FixedCharge,
  type FixedChargeInput,
} from '@shared/types/charges';
import { useRef, useState } from 'react';

import { Card } from '../components/Card';
import { ChargeForm } from '../components/ChargeForm';
import { DonutChart, type DonutSlice } from '../components/DonutChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile, type StatDelta } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { useChargeMutations, useChargesStats } from '../hooks/useCharges';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { todayInParis } from '../lib/dates';
import { volumeDelta } from '../lib/deltas';
import { formatDayLong, formatMoney, formatMonthShort } from '../lib/format';
import { CATEGORICAL_COLORS, OTHER_COLOR } from '../lib/theme';

const money = (value: number) => formatMoney(value, 'EUR');

/** Ce que coûte la charge par mois : la mensuelle telle quelle, un tiers d'une trimestrielle, un douzième d'une annuelle ; rien pour une ponctuelle. */
const monthlyEquivalent = (charge: FixedCharge): number | null =>
  charge.frequency === 'monthly' ? charge.amount
  : charge.frequency === 'quarterly' ? charge.amount / 3
  : charge.frequency === 'yearly' ? charge.amount / 12
  : null;

type ChargeStatus = 'active' | 'upcoming' | 'ended' | 'once_past' | 'once_future';

const STATUS_LABELS: Record<ChargeStatus, string> = {
  active: 'en cours',
  upcoming: 'à venir',
  ended: 'terminée',
  once_past: 'ponctuelle, passée',
  once_future: 'ponctuelle, à venir',
};

/** En cours d'abord, puis à venir, puis terminées ; les ponctuelles en dernier. */
const STATUS_ORDER: Record<ChargeStatus, number> = { active: 0, upcoming: 1, ended: 2, once_future: 3, once_past: 4 };

const statusOf = (charge: FixedCharge, today: string): ChargeStatus => {
  if (charge.frequency === 'once') return charge.startDate < today ? 'once_past' : 'once_future';
  if (charge.startDate > today) return 'upcoming';
  if (charge.endDate && charge.endDate < today) return 'ended';
  return 'active';
};

export function ChargesPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const query = useChargesStats({ from: period.from, to: period.to, token, onUnauthorized: signOut });
  const mutations = useChargeMutations();
  const [editing, setEditing] = useState<FixedCharge | null>(null);
  const [adding, setAdding] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const stats = query.data;
  const totals = stats?.comparison.current.totals;
  const overlap = stats?.comparison.comparisonsOverlap ?? false;
  const refs = overlap
    ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
    : [
        { label: 'période préc.', key: 'previous' as const },
        { label: "l'an dernier", key: 'yearAgo' as const },
      ];
  // Une charge qui monte n'est ni bonne ni mauvaise en soi : couleur neutre.
  const deltas = (pick: (t: ChargesTotals) => number): StatDelta[] =>
    refs.map(({ label, key }) => {
      const ref = stats?.comparison[key].totals;
      return volumeDelta({ label, available: (ref?.total ?? 0) > 0 }, totals ? pick(totals) : null, ref ? pick(ref) : null, money, null, 'aucune charge');
    });

  // Une couleur par catégorie, fixe : les salaires restent roses d'une période à l'autre.
  const categoryColor = (index: number) => CATEGORICAL_COLORS[index] ?? OTHER_COLOR;
  const slices: DonutSlice[] = CHARGE_CATEGORIES.map((category, index) => ({
    key: category,
    label: CHARGE_CATEGORY_LABELS[category],
    value: totals?.byCategory[category] ?? 0,
    color: categoryColor(index),
  })).filter((slice) => slice.value > 0);

  const today = todayInParis();
  const charges = [...(stats?.charges ?? [])]
    .map((charge) => ({ charge, status: statusOf(charge, today), monthly: monthlyEquivalent(charge) }))
    .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || (b.monthly ?? 0) - (a.monthly ?? 0) || a.charge.label.localeCompare(b.charge.label));
  // Ce que coûtent aujourd'hui les charges récurrentes en cours, par mois.
  const activeMonthly = charges.filter((row) => row.status === 'active').reduce((sum, row) => sum + (row.monthly ?? 0), 0);
  const activeCount = charges.filter((row) => row.status === 'active').length;

  // Formulaire visible : à la demande, pendant une modification, ou tant qu'il n'y a rien.
  const formOpen = adding || editing !== null || charges.length === 0;
  const openForm = (charge: FixedCharge | null) => {
    setEditing(charge);
    setAdding(true);
    // Le formulaire est sous la liste : on y descend, plutôt qu'en haut de page.
    window.setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0);
  };
  const closeForm = () => {
    setEditing(null);
    setAdding(false);
  };

  const handleSubmit = async (input: FixedChargeInput) => {
    if (editing) {
      await mutations.update(editing.id, input);
      closeForm();
    } else {
      await mutations.create(input);
    }
  };

  const handleDelete = async (charge: FixedCharge) => {
    if (!window.confirm(`Supprimer la charge « ${charge.label} » (${money(charge.amount)} ${CHARGE_FREQUENCY_LABELS[charge.frequency]}) ?`)) return;
    if (editing?.id === charge.id) closeForm();
    await mutations.remove(charge.id);
  };

  const usedCategories = CHARGE_CATEGORIES.filter((category) => (stats?.months ?? []).some((month) => month.byCategory[category] > 0));

  return (
    <SectionShell
      lastSyncAt={undefined}
      isPending={query.isPending}
      isFetching={query.isFetching}
      error={query.error}
      hasData={Boolean(stats)}
      title={
        <h1 className="page-title">
          Charges
          <span className="page-title__sub">Charges fixes saisies à la main : salaires, loyer, logiciels, prestataires…</span>
        </h1>
      }
    >
      <h2 className="section-heading">
        Charges saisies
        <span className="section-heading__sub">
          {activeCount > 0
            ? `${activeCount} en cours, ${money(activeMonthly)} HT par mois`
            : 'aucune charge récurrente en cours'}
        </span>
      </h2>
      <Card title="Toutes les charges" subtitle="En cours d'abord, puis à venir et terminées. Montants HT par échéance ; « par mois » ramène chaque charge récurrente au mois.">
        {charges.length === 0 ? (
          <p className="empty">Aucune charge saisie. Ajoutez-en une avec le formulaire ci-dessous.</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Libellé</th>
                  <th>Catégorie</th>
                  <th className="num">Montant HT</th>
                  <th>Fréquence</th>
                  <th className="num">Par mois HT</th>
                  <th>Du</th>
                  <th>Au</th>
                  <th>Statut</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {charges.map(({ charge, status, monthly }) => (
                  <tr key={charge.id} className={status === 'active' ? undefined : 'row-muted'}>
                    <td className="wrap">
                      {charge.label}
                      {charge.note && <span className="marker-note">{charge.note}</span>}
                    </td>
                    <td>{CHARGE_CATEGORY_LABELS[charge.category]}</td>
                    <td className="num">{formatMoney(charge.amount, 'EUR', true)}</td>
                    <td>{CHARGE_FREQUENCY_LABELS[charge.frequency]}</td>
                    <td className="num">{monthly === null ? '—' : money(monthly)}</td>
                    <td>{formatDayLong(charge.startDate)}</td>
                    <td>{charge.frequency === 'once' ? '—' : charge.endDate ? formatDayLong(charge.endDate) : 'en cours'}</td>
                    <td>{STATUS_LABELS[status]}</td>
                    <td className="num">
                      <button type="button" className="btn btn--small" disabled={mutations.isSaving} onClick={() => openForm(charge)}>
                        Modifier
                      </button>{' '}
                      <button type="button" className="btn btn--small" disabled={mutations.isSaving} onClick={() => void handleDelete(charge)}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {activeCount > 0 && (
                <tfoot>
                  <tr className="row-total">
                    <td colSpan={4}>Charges récurrentes en cours</td>
                    <td className="num">{money(activeMonthly)}</td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        <div className="card__actions">
          {formOpen ? (
            charges.length > 0 && (
              <button type="button" className="btn" onClick={closeForm} disabled={mutations.isSaving}>
                Fermer le formulaire
              </button>
            )
          ) : (
            <button type="button" className="btn btn--primary" onClick={() => openForm(null)}>
              + Ajouter une charge
            </button>
          )}
        </div>
      </Card>
      {formOpen && (
        <div ref={formRef}>
          <ChargeForm editing={editing} isSaving={mutations.isSaving} onSubmit={handleSubmit} onCancel={closeForm} />
        </div>
      )}

      <h2 className="section-heading">
        Charges de la période
        <span className="section-heading__sub">HT, chaque charge au prorata des jours de la période qu'elle couvre</span>
      </h2>
      <div className="kpi-row">
        <StatTile
          label="Total charges (HT)"
          value={money(totals?.total ?? 0)}
          deltas={deltas((t) => t.total)}
          hint="somme des charges lissées jour par jour sur la période ; retranchée dans le Résultat net et la MC4 du Résumé"
        />
        <StatTile
          label="En cours, par mois (HT)"
          value={money(activeMonthly)}
          hint="charges récurrentes en cours aujourd'hui, ramenées au mois (trimestrielle ÷ 3, annuelle ÷ 12) — indépendant de la période"
        />
        {CHARGE_CATEGORIES.filter((category) => (totals?.byCategory[category] ?? 0) > 0).map((category) => (
          <StatTile
            key={category}
            label={`${CHARGE_CATEGORY_LABELS[category]} (HT)`}
            value={money(totals?.byCategory[category] ?? 0)}
            deltas={deltas((t) => t.byCategory[category])}
          />
        ))}
      </div>

      <div className="chart-grid chart-grid--thirds">
        <DonutChart title="Par catégorie (HT)" subtitle="Part de chaque catégorie dans les charges de la période" slices={slices} formatValue={money} emptyMessage="Aucune charge sur cette période." />

        <Card title="Douze derniers mois (HT)" subtitle="Mois entiers, le plus récent en haut" className="chart-grid__two">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Mois</th>
                  {usedCategories.map((category) => (
                    <th key={category} className="num">
                      {CHARGE_CATEGORY_LABELS[category]}
                    </th>
                  ))}
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {[...(stats?.months ?? [])].reverse().map((month) => (
                  <tr key={month.month}>
                    <td>{formatMonthShort(`${month.month}-01`)}</td>
                    {usedCategories.map((category) => (
                      <td key={category} className="num">
                        {money(month.byCategory[category])}
                      </td>
                    ))}
                    <td className="num">
                      <strong>{money(month.total)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <p className="footnote">
        Montants HT. Une charge mensuelle est lissée jour par jour sur son mois (un loyer de 1 500 € compte
        1 500 € ÷ 30 chaque jour de septembre) : un mois entier redonne le montant, une semaine en compte sa
        part. Trimestrielle = un tiers par mois, annuelle = un douzième par mois ; une charge ponctuelle compte
        en entier le jour de sa date. Seuls les jours entre le début et la fin (incluse) comptent. Le total
        est retranché de la marge nette des canaux dans le « Résultat net » du Résumé.
      </p>
    </SectionShell>
  );
}
